import { useState } from "react";
import { useOrdersWithActiveReservations } from "../hooks/useOrders";
import type { ReservedOrderSummary, ReservedOrderItem, ReservationInstruction } from "../services/ordersApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, CheckCircle2, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";

const formatDate = (v?: string) => (v ? format(parseISO(v), "dd MMM yyyy") : "—");

const formatQty = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.00$/, "");

const getUnitLabel = (unit?: number, quantity = 0) => {
  const isSingular = Math.abs(quantity) === 1;
  switch (unit) {
    case 5:
      return isSingular ? "piece" : "pieces";
    case 11:
      return isSingular ? "tray" : "trays";
    default:
      return isSingular ? "unit" : "units";
  }
};

function InstructionRow({ instruction }: { instruction: ReservationInstruction }) {
  const unitLabel = getUnitLabel(instruction.unit, instruction.reservedQuantity);
  return (
    <TableRow className="bg-muted/20 hover:bg-muted/30">
      <TableCell className="px-2" />
      <TableCell className="px-2" />
      <TableCell className="px-2" />
      <TableCell className="pl-12 text-xs font-mono text-muted-foreground">
        {instruction.instructionNumber}
      </TableCell>
      <TableCell>
        <Badge
          variant={instruction.isReadyForDispatch ? "default" : "secondary"}
          className="text-xs"
        >
          {instruction.status}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{formatQty(instruction.reservedQuantity)} {unitLabel}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{instruction.greenhouseName || "—"}</TableCell>
      <TableCell>
        {instruction.isReadyForDispatch ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <Clock className="h-4 w-4 text-amber-500" />
        )}
      </TableCell>
    </TableRow>
  );
}

function ItemRow({
  item,
  isExpanded,
  onToggle,
}: {
  item: ReservedOrderItem;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const readyCount = item.reservedInstructions.filter((i) => i.isReadyForDispatch).length;
  const totalCount = item.reservedInstructions.length;
  const unitLabel = getUnitLabel(item.unit, item.reservedQuantity);

  return (
    <>
      <TableRow
        className="bg-muted/30 hover:bg-muted/40 cursor-pointer border-l-2 border-l-border"
        onClick={onToggle}
      >
        <TableCell className="px-2" />
        <TableCell className="px-2">
          {totalCount > 0 ? (
            isExpanded
              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <span className="w-3.5 inline-block" />
          )}
        </TableCell>
        <TableCell className="pl-4" colSpan={2}>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{item.productName}</span>
            {item.variety && (
              <span className="text-xs text-muted-foreground">{item.variety}</span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <span className="text-xs text-muted-foreground">
            {readyCount}/{totalCount} ready
          </span>
        </TableCell>
        <TableCell className="text-sm">
          {formatQty(item.reservedQuantity)} / {formatQty(item.orderedQuantity)} {unitLabel} reserved
        </TableCell>
        <TableCell />
        <TableCell>
          {readyCount === totalCount && totalCount > 0 ? (
            <Badge variant="default" className="text-xs">All Ready</Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">In Progress</Badge>
          )}
        </TableCell>
      </TableRow>

      {isExpanded && item.reservedInstructions.map((inst) => (
        <InstructionRow key={inst.sowingInstructionId} instruction={inst} />
      ))}
    </>
  );
}

function OrderRow({ order }: { order: ReservedOrderSummary }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) =>
    setExpandedItems((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50 font-medium"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell className="px-2">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell className="px-2" />
        <TableCell className="font-medium">{order.orderNumber}</TableCell>
        <TableCell>{order.customerName}</TableCell>
        <TableCell>
          {order.isFullyReady ? (
            <Badge variant="default" className="text-xs gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Ready for Dispatch
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs gap-1">
              <Clock className="h-3 w-3" />
              {formatQty(order.readyQuantity)}/{formatQty(order.totalReservedQuantity)} ready
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatQty(order.totalReservedQuantity)} reserved
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatDate(order.plannedPlantingDate)}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatDate(order.expectedDeliveryDate)}
        </TableCell>
      </TableRow>

      {expanded && order.items.map((item) => (
        <ItemRow
          key={item.orderItemId}
          item={item}
          isExpanded={expandedItems.has(item.orderItemId)}
          onToggle={() => toggleItem(item.orderItemId)}
        />
      ))}
    </>
  );
}

export const ReservedOrdersPage = () => {
  const query = useOrdersWithActiveReservations();

  const orders = query.data ?? [];
  const fullyReadyCount = orders.filter((o) => o.isFullyReady).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reserved Stock Orders</h1>
        <p className="text-muted-foreground mt-1">
          Orders with greenhouse stock reserved. Prioritize batches that are close to ready for dispatch.
        </p>
      </div>

      {/* Summary badges */}
      {orders.length > 0 && (
        <div className="flex gap-3">
          <Badge variant="default" className="text-sm px-3 py-1">
            {fullyReadyCount} fully ready for dispatch
          </Badge>
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {orders.length - fullyReadyCount} still growing
          </Badge>
        </div>
      )}

      {query.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading reserved orders...</p>
        </div>
      )}

      {query.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load reserved orders</p>
        </div>
      )}

      {!query.isLoading && orders.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">No orders currently have reserved stock</p>
        </div>
      )}

      {orders.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead className="w-8" />
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reserved</TableHead>
                <TableHead>Planting Date</TableHead>
                <TableHead>Delivery</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <OrderRow key={order.orderId} order={order} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
